import { DashboardPeriodo } from "../../../types/dashboard";

const pad = (n: number) => String(n).padStart(2, "0");

export const toISODate = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const startOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Segunda-feira (ISO, weekday 1) da semana que contém a data informada
const startOfIsoWeek = (date: Date): Date => {
  const d = startOfDay(date);
  const isoWeekday = d.getDay() === 0 ? 7 : d.getDay(); // domingo = 7
  d.setDate(d.getDate() - (isoWeekday - 1));
  return d;
};

/**
 * Calcula o intervalo [dataInicio, dataFim] de uma granularidade de período
 * a partir de uma data de referência. Espelha a lógica do backend
 * (DashboardAnalytics.php) para que front e back concordem nos limites.
 */
export function getPeriodRange(
  periodo: DashboardPeriodo,
  referenceDate: Date
): { dataInicio: string; dataFim: string } {
  const ref = startOfDay(referenceDate);

  switch (periodo) {
    case "diario": {
      const iso = toISODate(ref);
      return { dataInicio: iso, dataFim: iso };
    }
    case "semanal": {
      const monday = startOfIsoWeek(ref);
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      return { dataInicio: toISODate(monday), dataFim: toISODate(sunday) };
    }
    case "mensal": {
      const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
      const last = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
      return { dataInicio: toISODate(first), dataFim: toISODate(last) };
    }
    case "semestral": {
      const isFirstHalf = ref.getMonth() < 6; // Jan(0)..Jun(5)
      const first = new Date(ref.getFullYear(), isFirstHalf ? 0 : 6, 1);
      const last = new Date(ref.getFullYear(), isFirstHalf ? 6 : 12, 0);
      return { dataInicio: toISODate(first), dataFim: toISODate(last) };
    }
    case "anual": {
      const first = new Date(ref.getFullYear(), 0, 1);
      const last = new Date(ref.getFullYear(), 11, 31);
      return { dataInicio: toISODate(first), dataFim: toISODate(last) };
    }
    default: {
      const iso = toISODate(ref);
      return { dataInicio: iso, dataFim: iso };
    }
  }
}

/**
 * Move a data de referência para a instância anterior (-1) ou seguinte (+1)
 * da granularidade selecionada.
 */
export function shiftReferenceDate(
  periodo: DashboardPeriodo,
  referenceDate: Date,
  direction: 1 | -1
): Date {
  const d = new Date(referenceDate);

  switch (periodo) {
    case "diario":
      d.setDate(d.getDate() + direction);
      break;
    case "semanal":
      d.setDate(d.getDate() + direction * 7);
      break;
    case "mensal":
      d.setDate(1);
      d.setMonth(d.getMonth() + direction);
      break;
    case "semestral":
      d.setDate(1);
      d.setMonth(d.getMonth() + direction * 6);
      break;
    case "anual":
      d.setFullYear(d.getFullYear() + direction);
      break;
    default:
      break;
  }

  return d;
}

const MESES_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const MESES_PT_ABREV = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

const formatDayMonth = (d: Date) => `${d.getDate()} ${MESES_PT_ABREV[d.getMonth()]}`;
const formatDayMonthYear = (d: Date) => `${d.getDate()} ${MESES_PT_ABREV[d.getMonth()]} ${d.getFullYear()}`;

/**
 * Rótulo em pt-BR para exibir ao lado das setas de navegação de período.
 */
export function formatPeriodLabel(periodo: DashboardPeriodo, referenceDate: Date): string {
  const ref = startOfDay(referenceDate);

  switch (periodo) {
    case "diario":
      return formatDayMonthYear(ref);
    case "semanal": {
      const monday = startOfIsoWeek(ref);
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      return sunday.getFullYear() === monday.getFullYear()
        ? `${formatDayMonth(monday)} – ${formatDayMonthYear(sunday)}`
        : `${formatDayMonthYear(monday)} – ${formatDayMonthYear(sunday)}`;
    }
    case "mensal":
      return `${MESES_PT[ref.getMonth()]} ${ref.getFullYear()}`;
    case "semestral":
      return `${ref.getMonth() < 6 ? "1º" : "2º"} Semestre ${ref.getFullYear()}`;
    case "anual":
      return `${ref.getFullYear()}`;
    default:
      return "";
  }
}
